/**
 * ChatList.js — chat list screen.
 *
 * Reactive: subscribes to $filteredChats nanostore and re-renders whenever
 * any chat is added/updated/bumped to top. No manual DOM manipulation.
 */

import { html } from 'htm/preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { useStore } from '@nanostores/preact';

import { $filteredChats, $chatFilter, $chatSearch, $channelFilter } from '../stores/chats.js';
import { $session, isOwner } from '../stores/session.js';
import { openThread, navigate, showToast } from '../stores/ui.js';
import { loadChats } from '../services/chats-service.js';
import { listChannelsWithBadges, $channelMeta } from '../services/channel-colors.js';
import { reconnectRealtime } from '../services/realtime.js';

import { ChatRow } from './ChatRow.js';
import { PermissionDegradedBanner } from './PermissionDegradedBanner.js';
import { $pendingShare, setPendingShare } from '../stores/ui.js';

export function ChatList() {
  const chats         = useStore($filteredChats);
  const filter        = useStore($chatFilter);
  const channelFilter = useStore($channelFilter);
  const search        = useStore($chatSearch);
  const session       = useStore($session);
  const pendingShare  = useStore($pendingShare); // #193
  // Re-render when channel meta changes (color or label) on the server.
  // listChannelsWithBadges reads the same store so pills update live.
  useStore($channelMeta);

  const channels = listChannelsWithBadges();

  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);

  // #190 (revised) — pull-to-refresh on the chat list. Native browser
  // pull-to-refresh doesn't fire inside an installed PWA (display:standalone
  // hides the browser chrome it would attach to), so we wire our own. Used
  // by agents who came back from offline and want to manually retry the
  // connection without waiting for the next visibility tick.
  const scrollRef     = useRef(null);
  const ptrStartY     = useRef(null);
  const [ptrDist,    setPtrDist]    = useState(0);   // current pull distance, px
  const [refreshing, setRefreshing] = useState(false);
  const PTR_THRESHOLD = 60;   // distance the user must drag past to commit
  const PTR_MAX       = 120;  // visual cap so pulls don't run off the screen

  const runRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      reconnectRealtime();
      await loadChats({
        filter,
        agent: session?.agentEmail,
      });
      showToast('Refreshed', 1000);
    } catch (e) {
      showToast(e?.message || 'Refresh failed', 2200);
    } finally {
      setRefreshing(false);
      setPtrDist(0);
    }
  };

  const onPtrTouchStart = (e) => {
    if (!scrollRef.current) return;
    if (scrollRef.current.scrollTop > 0) return; // only at the top of the list
    ptrStartY.current = e.touches[0]?.clientY ?? null;
  };
  const onPtrTouchMove = (e) => {
    if (ptrStartY.current == null) return;
    const dy = (e.touches[0]?.clientY ?? 0) - ptrStartY.current;
    if (dy <= 0) { setPtrDist(0); return; }
    // Friction: ease the pull so it doesn't feel rubber-bandy past the cap.
    const eased = Math.min(PTR_MAX, dy * 0.6);
    setPtrDist(eased);
  };
  const onPtrTouchEnd = () => {
    if (ptrStartY.current == null) return;
    const committed = ptrDist >= PTR_THRESHOLD;
    ptrStartY.current = null;
    if (committed) runRefresh(); else setPtrDist(0);
  };

  // iOS "Add to Home Screen" prompt — only shown on Mobile Safari when the
  // site isn't already installed as a PWA, and the user hasn't dismissed it.
  // Computed once at mount so we don't re-check navigator on every render.
  const [showInstallHint, setShowInstallHint] = useState(() => {
    try {
      if (typeof navigator === 'undefined') return false;
      const ua = navigator.userAgent || '';
      const isIOS = /iPhone|iPad|iPod/.test(ua) && !window.MSStream;
      // navigator.standalone is the iOS-only "launched from home screen" flag.
      // display-mode:standalone covers Android + desktop PWA installs.
      const isStandalone = navigator.standalone === true
        || window.matchMedia?.('(display-mode: standalone)')?.matches;
      const dismissed = localStorage.getItem('inbox_v2_install_hint_dismissed') === '1';
      return isIOS && !isStandalone && !dismissed;
    } catch {
      return false;
    }
  });

  const dismissInstallHint = () => {
    try { localStorage.setItem('inbox_v2_install_hint_dismissed', '1'); } catch {}
    setShowInstallHint(false);
  };

  // Load chats on mount and when filter changes
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    loadChats({
      filter,
      agent: session?.agentEmail,
    }).then(() => {
      if (!cancelled) setLoading(false);
    }).catch(e => {
      if (!cancelled) {
        setError(e.message);
        setLoading(false);
      }
    });

    return () => { cancelled = true; };
  }, [filter]);

  // Search is client-side — filters the already-loaded chat list via
  // $filteredChats' `search` branch (substring contains on name + phone).
  // We intentionally do NOT hit the CRM /chats/search endpoint: that API
  // is scoped to custom field values, not name/phone, and returned empty
  // for every agent query (verified 2026-04-14). For an inbox with <500
  // chats loaded, client-side filter is instant and zero-network.
  // When the user closes the search, we repopulate the full list in case
  // they'd typed and their contacts weren't in the initial page.

  const handleChatClick = (chat) => {
    // #153: chat.id is the composite chat_key (contactId:channelId); chat.contactId
    // is the CRM ObjectId that all contact-scoped APIs (assign/done/close/tags/CF)
    // need. Pass both — Thread reads contactId from $openChatId for CRM calls and
    // chatKey from $openChatKey for store lookups.
    openThread({
      chatKey: chat.id,
      chatId: chat.contactId,
      phone: chat.phone,
      channelPhoneId: chat.channelPhoneId, // null only for orphan rows
    });
  };

  // Template re-engagement removed (#100 / #94 product decision):
  // agents only respond to active 24h-window customers, outbound
  // re-engagement happens through marketing surfaces. The session-closed
  // chat row no longer offers a "Send template" shortcut.

  const showUnassignedPill = isOwner() ||
    (session?.permissions || []).includes('live_chat.unassigned.ALL');

  return html`
    <div class="inbox-screen" style="display:flex">
      <!-- Header -->
      <div class="inbox-header">
        <img src="/assets/images/logo-512.png" alt="ODIV" width="28" height="28" class="inbox-header-logo" />
        <h2 class="inbox-header-title">Inbox</h2>
        <button class="inbox-header-icon-btn" onClick=${() => setSearchOpen(true)} title="Search">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8"/>
            <path d="m21 21-4.35-4.35"/>
          </svg>
        </button>
        <button class="inbox-header-icon-btn" onClick=${() => navigate('profile')} title="Profile">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
            <circle cx="12" cy="7" r="4"/>
          </svg>
        </button>
      </div>

      <!-- Search bar -->
      ${searchOpen && html`
        <div class="inbox-search-bar" style="display:flex">
          <input
            type="text"
            class="inbox-search-input"
            placeholder="Search by name…"
            value=${search}
            onInput=${(e) => $chatSearch.set(e.target.value)}
            autofocus
          />
          <button class="inbox-btn-link" onClick=${() => { setSearchOpen(false); $chatSearch.set(''); }}>Cancel</button>
        </div>
      `}

      <!-- Filter pills -->
      ${!searchOpen && html`
        <div class="inbox-filter-pills">
          ${['all', 'mine', 'unread'].map(f => html`
            <button
              key=${f}
              class="inbox-pill ${filter === f ? 'inbox-pill-active' : ''}"
              onClick=${() => $chatFilter.set(f)}
            >
              ${f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          `)}
          ${showUnassignedPill && html`
            <button
              class="inbox-pill ${filter === 'unassigned' ? 'inbox-pill-active' : ''}"
              onClick=${() => $chatFilter.set('unassigned')}
            >Unassigned</button>
          `}
        </div>
      `}

      <!-- Channel filter pills — only rendered when the account has more than
           one WhatsApp channel. Single-channel customers see nothing because
           the filter would be meaningless. Tapping a pill toggles that
           channel in/out of the view (click again = back to 'all'). -->
      ${!searchOpen && channels.length > 1 && html`
        <div
          class="inbox-channel-filter-pills"
          style="display:flex;gap:8px;padding:6px 12px 10px;overflow-x:auto;-webkit-overflow-scrolling:touch;scrollbar-width:none"
        >
          <button
            class="inbox-pill ${channelFilter === 'all' ? 'inbox-pill-active' : ''}"
            onClick=${() => $channelFilter.set('all')}
            style="flex-shrink:0;display:flex;align-items:center;gap:6px"
            title="All channels"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="2" y1="12" x2="22" y2="12"/>
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
            </svg>
            All
          </button>
          ${channels.map(ch => {
            const active = channelFilter === ch.channelId;
            // Composite display: if the owner has set a Channel Label
            // (e.g. "Sales"), prepend it as a small uppercase prefix:
            //   "SALES · Odiv AI Automations"
            // Otherwise just the channel name.
            return html`
              <button
                key=${ch.channelId}
                class="inbox-pill ${active ? 'inbox-pill-active' : ''}"
                onClick=${() => $channelFilter.set(active ? 'all' : ch.channelId)}
                title=${ch.label ? `${ch.label} · ${ch.displayName}` : ch.displayName}
                style="flex-shrink:0;display:flex;align-items:center;gap:7px;max-width:200px"
              >
                <span
                  style="display:inline-flex;align-items:center;justify-content:center;min-width:20px;height:16px;padding:0 4px;border-radius:8px;background:${ch.color};color:#fff;font-size:0.6rem;font-weight:700;letter-spacing:0.04em"
                >${ch.prefix}</span>
                <span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:160px">
                  ${ch.label && html`<span style="opacity:0.75;font-weight:700;letter-spacing:0.04em;text-transform:uppercase;font-size:0.62rem;margin-right:6px">${ch.label}</span>`}
                  ${ch.displayName}
                </span>
              </button>
            `;
          })}
        </div>
      `}

      <!-- iOS "Add to Home Screen" hint — only visible on Mobile Safari,
           and only until the user dismisses it once. Installed PWAs hide
           it automatically via display-mode:standalone detection. -->
      ${showInstallHint && html`
        <div
          class="inbox-install-hint"
          style="display:flex;align-items:center;gap:10px;margin:8px 12px 0;padding:10px 12px;background:rgba(37,211,102,0.10);border:1px solid rgba(37,211,102,0.35);border-radius:12px;font-size:0.78rem;color:#d1fae5"
          role="status"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#25D366" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="17 8 12 3 7 8"/>
            <line x1="12" y1="3" x2="12" y2="15"/>
          </svg>
          <div style="flex:1;min-width:0">
            <div style="font-weight:600;color:#fff">Install ODIV Inbox</div>
            <div style="opacity:0.8;line-height:1.35">
              Tap the
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="vertical-align:middle">
                <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
                <polyline points="16 6 12 2 8 6"/>
                <line x1="12" y1="2" x2="12" y2="15"/>
              </svg>
              Share icon, then <b>Add to Home Screen</b>.
            </div>
          </div>
          <button
            onClick=${dismissInstallHint}
            style="background:none;border:none;color:#7d8799;font-size:1.3rem;cursor:pointer;padding:0 4px;line-height:1;flex-shrink:0"
            title="Dismiss"
            aria-label="Dismiss install hint"
          >×</button>
        </div>
      `}

      <!-- #193 — Web Share Target picker banner. Visible whenever the agent
           landed via the OS share sheet with content waiting to be sent.
           Pick any chat from the list below; Composer reads the same
           $pendingShare atom on mount and pre-fills its input. -->
      ${pendingShare && html`
        <div class="inbox-share-banner" role="status" aria-live="polite">
          <div class="inbox-share-banner-icon" aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="18" cy="5" r="3"/>
              <circle cx="6" cy="12" r="3"/>
              <circle cx="18" cy="19" r="3"/>
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
            </svg>
          </div>
          <div class="inbox-share-banner-body">
            <div class="inbox-share-banner-title">Pick a chat to send</div>
            <div class="inbox-share-banner-preview">${pendingShare.text}</div>
          </div>
          <button
            class="inbox-share-banner-cancel"
            onClick=${() => setPendingShare(null)}
            aria-label="Cancel share"
            title="Cancel share"
            data-qr-id="share-cancel"
          >×</button>
        </div>
      `}

      <!-- #168 — degraded-permissions banner. Renders only for non-owner
           sessions when a background hydrate has hit a 403, pointing the
           agent at the dashboard permission checklist. -->
      <${PermissionDegradedBanner} />

      <!-- Chat list (with pull-to-refresh #190 revised). The PTR indicator
           is rendered ABOVE the list and pinned by transform so it doesn't
           push the scroll content; the list itself shifts down by ptrDist
           while pulling so the gesture feels physical. -->
      ${(ptrDist > 0 || refreshing) && html`
        <div
          class="inbox-ptr-indicator"
          style=${`opacity:${Math.min(1, (refreshing ? 1 : ptrDist / PTR_THRESHOLD))}`}
          data-qr-id="ptr-indicator"
        >
          <svg
            width="18" height="18" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"
            style=${refreshing
              ? 'animation:inbox-ptr-spin 800ms linear infinite'
              : `transform:rotate(${Math.min(360, ptrDist * 6)}deg);transition:transform 60ms`}
          >
            <polyline points="23 4 23 10 17 10"/>
            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
          </svg>
          <span>${refreshing ? 'Refreshing…' : (ptrDist >= PTR_THRESHOLD ? 'Release to refresh' : 'Pull to refresh')}</span>
        </div>
      `}
      <div
        class="inbox-chat-list"
        ref=${scrollRef}
        onTouchStart=${onPtrTouchStart}
        onTouchMove=${onPtrTouchMove}
        onTouchEnd=${onPtrTouchEnd}
        onTouchCancel=${onPtrTouchEnd}
        style=${ptrDist > 0 && !refreshing
          ? `transform:translateY(${ptrDist}px);transition:transform 80ms`
          : (refreshing ? 'transform:translateY(36px);transition:transform 200ms' : '')}>
        ${loading && html`
          <div class="inbox-loading-state">
            <div class="inbox-spinner"></div>
            <span>Loading conversations…</span>
          </div>
        `}

        ${error && !loading && html`
          <div class="inbox-empty-state">
            <p style="color:#ef4444">Failed to load: ${error}</p>
          </div>
        `}

        ${!loading && !error && chats.length === 0 && html`
          <div class="inbox-empty-state">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" style="color:#7d8799">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
            ${(() => {
              // Distinguish three empty states: brand-new account
              // (no chats anywhere), filter-suppressed (chats exist but
              // current filter hides all of them), and search miss.
              if (search) {
                return html`
                  <p style="font-weight:600;margin-bottom:0.25rem">No matches</p>
                  <p style="font-size:0.8rem;color:#7d8799;line-height:1.4;max-width:280px;text-align:center">
                    Nothing matches "${search}". Try a different name or phone number.
                  </p>
                `;
              }
              if (channelFilter !== 'all') {
                const ch = channels.find(c => c.channelId === channelFilter);
                const label = ch ? (ch.label ? `${ch.label} · ${ch.displayName}` : ch.displayName) : 'this channel';
                return html`
                  <p style="font-weight:600;margin-bottom:0.25rem">No chats on ${label}</p>
                  <p style="font-size:0.8rem;color:#7d8799;line-height:1.4;max-width:280px;text-align:center">
                    No customers have messaged via this channel yet. Try the All filter to see chats from other channels.
                  </p>
                  <button
                    onClick=${() => $channelFilter.set('all')}
                    class="inbox-btn-link"
                    style="font-size:0.8rem;margin-top:0.5rem;background:none;border:none;cursor:pointer;color:var(--inbox-accent,#22d3a8)"
                  >Show all channels →</button>
                `;
              }
              if (filter === 'unread') {
                return html`
                  <p style="font-weight:600;margin-bottom:0.25rem">All caught up</p>
                  <p style="font-size:0.8rem;color:#7d8799;line-height:1.4;max-width:280px;text-align:center">
                    No unread messages right now.
                  </p>
                `;
              }
              if (filter === 'unassigned') {
                return html`
                  <p style="font-weight:600;margin-bottom:0.25rem">No unassigned chats</p>
                  <p style="font-size:0.8rem;color:#7d8799;line-height:1.4;max-width:280px;text-align:center">
                    Every conversation already has an owner.
                  </p>
                `;
              }
              if (filter === 'mine') {
                return html`
                  <p style="font-weight:600;margin-bottom:0.25rem">No chats assigned to you</p>
                  <p style="font-size:0.8rem;color:#7d8799;line-height:1.4;max-width:280px;text-align:center">
                    Conversations you're assigned to will appear here.
                  </p>
                `;
              }
              // Brand-new account fallback. The "Open platform" link is
              // owner-only — non-owner agents (#164 + #168) shouldn't
              // see any path out of the PWA into the CRM web UI; if a
              // team member is staring at an empty list it's the admin
              // who needs to act, not them.
              return html`
                <p style="font-weight:600;margin-bottom:0.25rem">No conversations yet</p>
                <p style="font-size:0.8rem;color:#7d8799;line-height:1.4;max-width:280px;text-align:center">
                  ${isOwner()
                    ? 'Make sure your webhook is configured in the platform.'
                    : 'New conversations will appear here as customers message in.'}
                </p>
                ${isOwner() && html`
                  <a href="https://chat.odivend.com" target="_blank" rel="noopener" class="inbox-btn-link" style="font-size:0.8rem;margin-top:0.5rem">
                    Open platform to configure →
                  </a>
                `}
              `;
            })()}
          </div>
        `}

        ${!loading && !error && chats.length > 0 && html`
          <div>
            ${chats.map(chat => html`
              <${ChatRow} key=${chat.id} chat=${chat} onClick=${handleChatClick} />
            `)}
          </div>
        `}
      </div>
    </div>
  `;
}
